import type { HTMLAttributes, JSX, ReactNode, Ref } from 'react'
import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/cn'

/**
 * Поверхность: карточка, панель, плитка.
 *
 * Главное отличие от прошлой версии — КЛИКАБЕЛЬНОСТЬ ЦЕЛИКОМ. Раньше по
 * карточке бота нельзя было кликнуть: ссылкой было только название, и приходилось
 * прицеливаться в текст. Здесь ссылка растягивается на всю площадь через
 * псевдоэлемент (`stretched link`), а кнопки внутри поднимаются над ней
 * (`.on-top`) и продолжают работать.
 *
 * Почему не «обернуть всё в <a>»: внутри карточки есть кнопки и меню, а кнопка
 * внутри ссылки — невалидная разметка, которая ломает и клавиатуру, и
 * скринридеры.
 */

type Variant = 'flat' | 'raised' | 'accent'

const VARIANT: Record<Variant, string> = {
  flat: 'bg-panel shadow-sm',
  // Градиент + светлая кромка сверху = «поверхность под лампой»: так панели с
  // данными отличаются от плоских списков.
  raised:
    'bg-gradient-to-b from-panel-2 to-panel shadow-md before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-line-strong before:to-transparent',
  accent: 'bg-gradient-to-b from-panel-2 to-panel shadow-md border-honey/35'
}

export interface SurfaceProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * В React 19 `ref` — обычный проп функционального компонента, `forwardRef`
   * больше не нужен. Нужен, например, камере: она измеряет свой прямоугольник
   * для FLIP-анимации разворота.
   */
  ref?: Ref<HTMLDivElement>
  variant?: Variant
  /** Куда ведёт клик по всей площади. Взаимоисключимо с `onActivate`. */
  to?: string
  params?: Record<string, string>
  /** Клик по всей площади без навигации. */
  onActivate?: () => void
  /** Доступное имя для растянутой ссылки — иначе она безымянная. */
  activateLabel?: string
  children?: ReactNode
}

export function Surface({
  ref,
  variant = 'flat',
  to,
  params,
  onActivate,
  activateLabel,
  className,
  children,
  ...rest
}: SurfaceProps): JSX.Element {
  const clickable = to !== undefined || onActivate !== undefined

  return (
    <div
      ref={ref}
      className={cn(
        'relative overflow-hidden rounded-md border border-line',
        VARIANT[variant],
        clickable && 'hoverable',
        // `focus-within` кольцо: сама карточка не в табе, но когда фокус попадает
        // на её растянутую ссылку — рамка обязана быть видна.
        clickable && 'focus-within:border-honey/60',
        className
      )}
      {...rest}
    >
      {to !== undefined ? (
        <Link
          to={to}
          params={params}
          aria-label={activateLabel}
          // Растянутая ссылка: занимает всю карточку, но лежит НИЖЕ кнопок.
          className="absolute inset-0 z-raised"
        />
      ) : null}

      {to === undefined && onActivate !== undefined ? (
        <button
          type="button"
          aria-label={activateLabel}
          onClick={onActivate}
          className="absolute inset-0 z-raised cursor-pointer"
        />
      ) : null}

      {children}
    </div>
  )
}

/**
 * Обёртка для интерактива внутри кликабельной карточки: поднимает элементы над
 * растянутой ссылкой. Без неё кнопки «стоп» и «меню» перехватывались бы ссылкой.
 */
export function OnTop({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={cn('relative z-sticky', className)} {...rest}>
      {children}
    </div>
  )
}

export interface PanelProps {
  title: string
  /** Правый верхний угол: фильтры, переключатели, кнопки. */
  actions?: ReactNode
  /** Приписка под заголовком: единицы, период, уточнение. */
  note?: string
  children: ReactNode
  className?: string
  bodyClassName?: string
}

/** Панель с заголовком — основной строительный блок экранов. */
export function Panel({
  title,
  actions,
  note,
  children,
  className,
  bodyClassName
}: PanelProps): JSX.Element {
  return (
    <Surface variant="raised" className={cn('flex flex-col', className)}>
      <div className="flex min-h-12 flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="truncate font-display text-base font-semibold text-ink">{title}</h2>
          {note === undefined ? null : (
            <span className="shrink-0 text-2xs text-ink-muted">{note}</span>
          )}
        </div>
        {actions === undefined ? null : (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
      <div className={cn('min-w-0 p-4', bodyClassName)}>{children}</div>
    </Surface>
  )
}

/**
 * Заголовок секции экрана — вне панели, для групп панелей.
 *
 * `title` необязателен намеренно: имя страницы уже стоит в хедере каркаса, и на
 * экранах, где секция одна («Аккаунты», «Боты»), второй такой же заголовок —
 * просто дубль. Тогда остаётся только строка действий.
 */
export function SectionHead({
  title,
  actions
}: {
  title?: string
  actions?: ReactNode
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-3',
        title === undefined ? 'justify-end' : 'justify-between'
      )}
    >
      {title === undefined ? null : (
        <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>
      )}
      {actions === undefined ? null : (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  )
}
